import { create } from "zustand";
import { environment } from "@/lib/environment";
import { ConsentCancelledError, isConsentCancelledError } from "@/lib/feed/consent-errors";

export type ParentHost = "web" | "teams";

interface TeamsHostState {
	readonly host: ParentHost;
	readonly initialized: boolean;
	readonly loginHint: string | null;
	setHost: (host: ParentHost, loginHint?: string | null) => void;
}

export const useTeamsHostStore = create<TeamsHostState>((set) => ({
	host: "web",
	initialized: false,
	loginHint: null,
	setHost: (host, loginHint = null) => {
		set({ host, loginHint, initialized: true });
	},
}));

export const AUTH_REDIRECT_ROUTE_KEY = "tg.auth.redirectRoute";

const AUTH_TRANSITION_PARAMS = [
	"code",
	"state",
	"error",
	"error_description",
	"session_state",
	"client_info",
	"result",
];

export const hasAuthTransitionParams = (
	params: URLSearchParams = new URLSearchParams(window.location.search)
): boolean => AUTH_TRANSITION_PARAMS.some((key) => params.has(key));

export const cachePreAuthRoute = (): void => {
	if (typeof window === "undefined") {
		return;
	}
	const params = new URLSearchParams(window.location.search);
	if (hasAuthTransitionParams(params)) {
		return;
	}
	const route = `${window.location.pathname}${window.location.search}${window.location.hash}`;
	if (!route.startsWith("/")) {
		return;
	}
	localStorage.setItem(AUTH_REDIRECT_ROUTE_KEY, route);
};

export const consumePreAuthRoute = (fallback = "/timer"): string => {
	const cached = localStorage.getItem(AUTH_REDIRECT_ROUTE_KEY);
	localStorage.removeItem(AUTH_REDIRECT_ROUTE_KEY);
	if (cached?.startsWith("/") && !cached.startsWith("//")) {
		return cached;
	}
	return fallback;
};

let teamsHostCache: boolean | null = null;
let teamsLoginHintCache: string | null | undefined;
let teamsSdkReady = false;

const persistTeamsSessionFlag = (): void => {
	try {
		sessionStorage.setItem("teams", "true");
	} catch {
		// ignore
	}
};

const clearTeamsSessionFlag = (): void => {
	try {
		sessionStorage.removeItem("teams");
	} catch {
		// ignore
	}
};

const readTeamsSessionFlag = (): boolean => {
	try {
		return JSON.parse(sessionStorage.getItem("teams") || "false") === true;
	} catch {
		return false;
	}
};

/**
 * Angular parity: only treat as Teams when query/session/Teams routes say so.
 * Never probe teams-js on plain web - initialize() throws "No Parent window found".
 */
const hasTeamsHint = (): boolean => {
	if (typeof window === "undefined") {
		return false;
	}
	const params = new URLSearchParams(window.location.search);
	// Angular index.html: `in_teams != undefined` (any value)
	if (params.has("in_teams")) {
		return true;
	}
	if (readTeamsSessionFlag()) {
		return true;
	}
	const path = window.location.pathname;
	if (path === "/teams-tabs-config" || path.startsWith("/teams-tabs-config/") || path.startsWith("/teams/")) {
		return true;
	}
	return false;
};

const markWebHost = (): "web" => {
	teamsHostCache = false;
	useTeamsHostStore.getState().setHost("web", null);
	return "web";
};

const markTeamsHost = (): "teams" => {
	teamsHostCache = true;
	persistTeamsSessionFlag();
	useTeamsHostStore.getState().setHost("teams", teamsLoginHintCache ?? null);
	return "teams";
};

/** Detect Microsoft Teams host (query / session / /teams/* routes + teams-js). */
export const resolveAuthHost = async (): Promise<"web" | "teams"> => {
	if (typeof window === "undefined") {
		return "web";
	}

	if (teamsHostCache === false) {
		return markWebHost();
	}

	if (teamsHostCache === true && teamsSdkReady) {
		return markTeamsHost();
	}

	if (!hasTeamsHint()) {
		return markWebHost();
	}

	try {
		await ensureTeamsSdk();
		return markTeamsHost();
	} catch {
		clearTeamsSessionFlag();
		teamsSdkReady = false;
		teamsLoginHintCache = undefined;
		return markWebHost();
	}
};

export const isTeamsHost = async (): Promise<boolean> => (await resolveAuthHost()) === "teams";

/**
 * Import + initialize only. No getContext round-trip, so callers inside a click
 * handler keep transient user activation for authenticate().
 */
export const loadTeamsSdk = async (): Promise<typeof import("@microsoft/teams-js")> => {
	const teams = await import("@microsoft/teams-js");
	if (!teamsSdkReady) {
		await teams.app.initialize();
		try {
			teams.app.notifySuccess();
		} catch {
			// ignore when host already notified
		}
		teamsSdkReady = true;
	}
	return teams;
};

export const ensureTeamsSdk = async (): Promise<typeof import("@microsoft/teams-js")> => {
	const teams = await loadTeamsSdk();

	try {
		const context = await teams.app.getContext();
		const hint = context.user?.loginHint || context.user?.userPrincipalName || null;
		teamsLoginHintCache = hint;
	} catch {
		teamsLoginHintCache = null;
	}

	return teams;
};

export const getTeamsLoginHint = async (): Promise<string | null> => {
	if ((await resolveAuthHost()) !== "teams") {
		return null;
	}
	if (teamsLoginHintCache !== undefined) {
		return teamsLoginHintCache;
	}
	try {
		await ensureTeamsSdk();
	} catch {
		return null;
	}
	return teamsLoginHintCache ?? null;
};

export const notifyTeamsAppLoaded = async (): Promise<void> => {
	if ((await resolveAuthHost()) !== "teams") {
		return;
	}
	try {
		const teams = await ensureTeamsSdk();
		teams.app.notifyAppLoaded();
	} catch {
		// ignore
	}
};

/**
 * Teams blocks / restricts MSAL popups. Use teams authentication.authenticate
 * against /teams-auth.html (same contract as Angular).
 *
 * Must be called straight from a user gesture: Teams opens a real popup window
 * and the host rejects the request once transient activation has expired.
 */
export const authenticateScopesInTeams = async (input: {
	readonly scopes: readonly string[];
	readonly apiScope?: string | null;
	readonly prompt?: "consent" | "select_account";
}): Promise<void> => {
	const teams = await loadTeamsSdk();

	const scopeString = input.scopes.join(",");
	localStorage.setItem("teams_auth_scopes", scopeString);
	localStorage.setItem("teams_auth_clientId", environment.VITE_AZURE_CLIENT_ID);
	if (input.apiScope) {
		localStorage.setItem("teams_auth_scope_url", input.apiScope);
	}

	const params = new URLSearchParams({
		closeOnSuccess: "true",
		clientId: environment.VITE_AZURE_CLIENT_ID,
		scopes: scopeString,
	});
	if (input.prompt) {
		params.set("type", input.prompt);
	}

	try {
		await teams.authentication.authenticate({
			url: `${window.location.origin}/teams-auth.html?${params.toString()}`,
		});
	} catch (error: unknown) {
		const message = error instanceof Error ? error.message : String(error);
		// teams-js rejects with "CancelledByUser" on dismiss, and with whatever
		// string notifyFailure sent otherwise. Only the former is a cancel.
		if (isConsentCancelledError(error) || isConsentCancelledError({ message })) {
			throw new ConsentCancelledError(message);
		}
		throw new Error(`Teams authentication failed: ${message}`);
	}
};
